"use client";

import CommonCard from "@/components/commonCard/CommonCard";
import NewsLetter from "@/components/newsletter/NewsLetter";
import AdSpace from "@/components/ads/Adspace";
import PostSkeleton from "@/components/skeletons/PostSkeleton";
import Breadcrumbs from "@/components/breadCrumbAndButton/BreadCrumbAndButton";
import { useState, useEffect } from "react";
import Image from "next/image";
import type { AdItem } from "@/lib/getAdSpaceData";
import type { AuthorInfo, AuthorPost } from "@/lib/getAuthorData";
import type { Post } from "@/types/post.types";
import "./style.css";
import { usePageType } from "@/lib/pageTypeContext";

interface Props {
  author: AuthorInfo;
  initialPosts: AuthorPost[];
  totalCount: number;
  hasMoreInitially: boolean;
  breakingNews: Post[];
  featuredPosts: Post[];
  initialAds: AdItem[];
}

const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL || "";
const PAGE_LIMIT = 16;

const getImage = (item: AuthorPost): string => {
  const path =
    item.image_url?.trim() ||
    (item.image_big?.trim() ? API_BASE_URL + item.image_big.trim() : "") ||
    (item.image_mid?.trim() ? API_BASE_URL + item.image_mid.trim() : "") ||
    (item.image_small?.trim() ? API_BASE_URL + item.image_small.trim() : "");
  return path || "/image-not-found.png";
};

export default function AuthorArchive({
  author,
  initialPosts,
  totalCount,
  hasMoreInitially,
  initialAds,
}: Props) {
  const [displayedPosts, setDisplayedPosts] =
    useState<AuthorPost[]>(initialPosts);
  const [hasMore, setHasMore] = useState(hasMoreInitially);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasAd, setHasAd] = useState(false);

  const { setPageType } = usePageType();

  // Signal to SubHeader that this is an author page
  useEffect(() => {
    setPageType("author");
    return () => setPageType("default");
  }, [setPageType]);

  const avatarUrl = author.avatar
    ? author.avatar.startsWith("http")
      ? author.avatar
      : `${API_BASE_URL}/${author.avatar.replace(/^\//, "")}`
    : null;

  const coverImage = author.cover_image?.trim()
    ? author.cover_image.trim().startsWith("http")
      ? author.cover_image.trim()
      : `${API_BASE_URL}/${author.cover_image.trim().replace(/^\//, "")}`
    : "/Cover.webp";

  const handleLoadMore = async () => {
    if (loadingMore || !hasMore) return;

    const nextPage = page + 1;
    setLoadingMore(true);

    try {
      const res = await fetch(
        `/api/author-news?author=${author.slug}&page=${nextPage}&limit=${PAGE_LIMIT}`,
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();
      const newPosts: AuthorPost[] = data?.posts ?? [];

      if (typeof data?.hasMore === "boolean") {
        setHasMore(data.hasMore);
      } else {
        if (newPosts.length < PAGE_LIMIT) setHasMore(false);
      }

      setDisplayedPosts((prev) => [...prev, ...newPosts]);
      setPage(nextPage);
    } catch (err) {
      console.error("Load more failed:", err);
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <>
      <div className="top-header-padding">
        <div className="gh-container">
          {/* Breadcrumbs */}
          <div className="pt-md-4 pb-3 mb-2">
            <Breadcrumbs
              items={[
                {
                  label: "Home",
                  href: process.env.NEXT_PUBLIC_SITE_URL ?? "/",
                },
                { label: author.username },
              ]}
            />
          </div>

          {/* Top Ad */}
          <AdSpace
            slot="post_top"
            viewport="auto"
            initialAds={initialAds}
            onRender={(rendered) => setHasAd(rendered)}
          />

          <div className="pb-5">
            <div className="row">
              <div className="col-12">
                <main className="post-page-main category-page">
                  {/* ── Author Header ──────────────────────────────────────── */}
                  <div className="category-page-header with-image mb-4">
                    <div
                      style={{
                        position: "relative",
                        width: "100%",
                        minHeight: "220px",
                        height: "478px",
                        borderRadius: "12px",
                        overflow: "hidden",
                        background: "#111",
                        display: "flex",
                        alignItems: "center",
                        padding:
                          "clamp(24px, 4vw, 48px) clamp(20px, 4vw, 56px)",
                      }}
                    >
                      {/* Subtle dark background */}
                      <div
                        style={{
                          position: "absolute",
                          inset: 0,
                          // backgroundImage: ` url(${coverImage})`,
                          background: `linear-gradient(
                            135deg,
                            rgba(26,26,46,0.75) 0%,
                            rgba(22,33,62,0.75) 50%,
                            rgba(15,52,96,0.75) 100%
                          ),
                          url(${coverImage}) center/cover no-repeat`,
                          backgroundPosition: "center",
                          backgroundSize: "cover",
                          backgroundRepeat: "no-repeat",
                        }}
                      />

                      {/* Content row */}
                      {/* Wrapper */}
                      <div style={{ position: "relative", width: "100%" }}>
                        {/* Row: Avatar + Name */}
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "clamp(16px, 3vw, 32px)",
                            width: "100%",
                            marginBottom: "16px",
                          }}
                        >
                          {/* Avatar */}
                          <div
                            style={{
                              flexShrink: 0,
                              width: "clamp(72px, 10vw, 112px)",
                              height: "clamp(72px, 10vw, 112px)",
                              borderRadius: "50%",
                              overflow: "hidden",
                              border: "3px solid rgba(255,255,255,0.25)",
                              background: "#333",
                            }}
                          >
                            {avatarUrl ? (
                              <Image
                                src={avatarUrl}
                                alt={author.username}
                                width={112}
                                height={112}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                            ) : (
                              <div
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontSize: "clamp(24px, 4vw, 40px)",
                                  fontWeight: 700,
                                  color: "#fff",
                                  background: "#ffa82d",
                                }}
                              >
                                {author.username.charAt(0).toUpperCase()}
                              </div>
                            )}
                          </div>

                          {/* Name + Count */}
                          <div style={{ flex: 1 }}>
                            <h1
                              style={{
                                fontSize: "clamp(24px, 3.5vw, 42px)",
                                fontWeight: 700,
                                color: "#fff",
                                margin: "0 0 6px",
                              }}
                            >
                              {author.username}
                            </h1>
                          </div>
                        </div>

                        {/* ✅ Description BELOW */}
                        <p className="author-description">
                          {author.about?.trim()
                            ? author.about
                            : `${author.username} is a writer for Coinccino and covers topics related to crypto, blockchain, markets, and innovation.`}
                        </p>
                        <span
                          style={{
                            fontSize: "clamp(13px, 1vw, 16px)",
                            fontWeight: 600,
                            color: "rgba(255,255,255,0.6)",
                          }}
                        >
                          {totalCount > 0
                            ? `${totalCount} Posts`
                            : `${displayedPosts.length}${hasMore ? "+" : ""} Posts`}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* ── Posts Grid ─────────────────────────────────────────── */}
                  <section className="archive-feed-wrapper">
                    <div className="row g-4">
                      {displayedPosts.map((post, index) => (
                        <div
                          className="col-12 col-md-4 card-container px-0 mx-0"
                          key={post.id ?? index}
                        >
                          <CommonCard
                            bodyClassName="px-0"
                            titleClassName="post-card-text section-card px-0 mx-0"
                            authorName={post.author_username || ""}
                            publishDate={post.created_at || ""}
                            titleText={post.title || ""}
                            imgSrc={getImage(post)}
                            category_name={post.category_name || ""}
                            category_slug={post.category_slug || ""}
                            category_color={post.category_color || ""}
                            sub_category_name={post.sub_category_name || ""}
                            sub_category_slug={post.sub_category_slug || ""}
                            sub_category_color={post.sub_category_color || ""}
                            cardLink={`/${post.title_slug}` || "#"}
                            responsiveimageClassName="responsive-image"
                            imageWrapperClassName="responsive-image-wrapper"
                            authorSlug={`${post.author_slug}` || ""}
                            authorSlugClassName="authors-slug"
                          />
                        </div>
                      ))}
                    </div>
                  </section>

                  {/* ── Load More ──────────────────────────────────────────── */}
                  <div className="text-center mt-md-3 px-md-3 px-2">
                    {loadingMore && (
                      <div className="row g-3">
                        {Array.from({ length: 3 }).map((_, i) => (
                          <div className="col-12 col-md-4" key={i}>
                            <PostSkeleton />
                          </div>
                        ))}
                      </div>
                    )}
                    {!loadingMore && hasMore && (
                      <div className="d-flex justify-content-center mt-2 mb-4">
                        <button
                          className="newsletter-submit-loading-button"
                          onClick={handleLoadMore}
                        >
                          Load More
                        </button>
                      </div>
                    )}
                  </div>
                </main>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Ad */}
        <AdSpace
          slot="post_bottom"
          viewport="auto"
          initialAds={initialAds}
          onRender={(rendered) => setHasAd(rendered)}
        />

        <NewsLetter />
      </div>
    </>
  );
}
